/** Dialog for uploading, previewing, and removing a staff member's e-signature. */

import { Pencil, Trash2 } from 'lucide-react'
import { useRef, useState } from 'react'

import { Button } from '@/frontend/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/frontend/components/ui/dialog'
import { Skeleton } from '@/frontend/components/ui/skeleton'
import { useGetStaffEsignature } from '@/frontend/hooks/api/use-get-staff-esignature'
import { usePatchAdminStaff } from '@/frontend/hooks/api/use-patch-admin-staff'
import { resizeImageToDataUrl } from '@/frontend/lib/images'

/**
 * Dialog for managing a staff member's e-signature.
 * Fetches the current signature on mount via useGetStaffEsignature,
 * and handles upload/replace/remove via the admin staff PATCH endpoint.
 */
export function StaffEsignatureDialog({
  staffId,
  open,
  onOpenChange,
}: {
  staffId: string
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { data: esignatureDataUrl, isLoading } = useGetStaffEsignature(staffId)
  const { mutateAsync: patchStaff } = usePatchAdminStaff()

  const [localPreview, setLocalPreview] = useState<string | null | undefined>(undefined)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Use local preview if set (after upload/remove), otherwise fall back to fetched data.
  const preview = localPreview !== undefined ? localPreview : (esignatureDataUrl ?? null)

  async function handleFile(file: File) {
    setUploading(true)
    setError(null)
    try {
      const dataUrl = await resizeImageToDataUrl(file)
      await patchStaff({ id: staffId, esignatureBase64: dataUrl })
      setLocalPreview(dataUrl)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Upload failed')
    } finally {
      setUploading(false)
    }
  }

  async function handleRemove() {
    setUploading(true)
    setError(null)
    try {
      await patchStaff({ id: staffId, esignatureBase64: null })
      setLocalPreview(null)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Remove failed')
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-sm">
        <DialogHeader>
          <DialogTitle>E-Signature</DialogTitle>
        </DialogHeader>

        <div className="flex flex-col gap-3">
          {isLoading ? (
            <Skeleton className="h-20 w-full rounded" />
          ) : preview ? (
            <div className="relative">
              <img
                src={preview}
                alt="E-signature preview"
                className="max-h-20 w-full rounded border border-border object-contain"
              />
              <div className="absolute bottom-1 right-1 flex gap-1">
                <Button
                  size="icon"
                  variant="secondary"
                  className="size-7"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Pencil className="size-3" />
                </Button>
                <Button
                  size="icon"
                  variant="destructive"
                  className="size-7"
                  disabled={uploading}
                  onClick={handleRemove}
                >
                  <Trash2 className="size-3" />
                </Button>
              </div>
            </div>
          ) : (
            <Button
              variant="outline"
              disabled={uploading}
              onClick={() => fileInputRef.current?.click()}
            >
              {uploading ? 'Uploading...' : 'Upload E-Signature'}
            </Button>
          )}

          {error && <p className="text-sm text-destructive-fg">{error}</p>}

          <p className="text-sm text-muted-foreground">
            PNG or JPEG, max 200KB after resize. Will be embedded in printed prescriptions.
          </p>

          <input
            ref={fileInputRef}
            type="file"
            accept="image/png,image/jpeg"
            className="hidden"
            onChange={(e) => {
              const file = e.target.files?.[0]
              if (!file) return
              e.target.value = ''
              handleFile(file)
            }}
          />
        </div>
      </DialogContent>
    </Dialog>
  )
}
